import React from "react";
import { groupThousands } from "../utils/math_helpers";

/**
 * Числовое поле с разделителями тысяч (пробел) прямо при вводе: «20 258 000».
 * В onChange отдаёт «сырое» значение (только цифры, ведущий минус, опц. точка) —
 * состояние в родителе хранится без пробелов. Курсор сохраняется по числу цифр слева.
 *
 * `money` — денежная сумма с копейками: дробная часть не длиннее 2 знаков,
 * разделитель вводится и запятой, и точкой, показывается запятой («1 250 000,50»).
 * Копейки всегда видны: «5 000 000» показывается как «5 000 000,00».
 */
export default function NumField({
  value, onChange, allowDecimal = false, money = false, className = "", placeholder, disabled, title, id,
}: {
  value: string | number | null | undefined;
  onChange: (raw: string) => void;
  allowDecimal?: boolean;
  money?: boolean;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  title?: string;
  id?: string;
}) {
  const ref = React.useRef<HTMLInputElement | null>(null);
  const decimal = allowDecimal || money;
  const raw = value === null || value === undefined ? "" : String(value);
  // Незавершённый ввод («1 250,» / «1 250,50» → 1250.5): если родитель хранит число,
  // из value эти хвосты не восстановить — показываем черновик, пока он равен value.
  const [draft, setDraft] = React.useState(raw);
  const sameNum = draft === raw || (draft !== "" && raw !== "" && Number(draft) === Number(raw));
  const shown = sameNum ? draft : raw;
  const [focused, setFocused] = React.useState(false);

  // «5000000» / «5000000.5» → «5000000.00» / «5000000.50»; в onChange не уходит — число то же.
  const pad = (s: string): string => {
    if (!money || s === "" || s === "-") return s;
    const [int, frac = ""] = s.split(".");
    return `${int || "0"}.${frac.padEnd(2, "0")}`;
  };

  const strip = (s: string): string => {
    let t = s.replace(/\s/g, "");
    if (decimal) t = t.replace(/,/g, ".");
    t = t.replace(decimal ? /[^\d.-]/g : /[^\d-]/g, "");
    const neg = t.startsWith("-");
    t = t.replace(/-/g, "").replace(/^0+(?=\d)/, "");   // «01 250» → «1 250»
    if (decimal) {
      const i = t.indexOf(".");
      if (i >= 0) {
        let frac = t.slice(i + 1).replace(/\./g, "");
        if (money) frac = frac.slice(0, 2);
        t = t.slice(0, i + 1) + frac;
      }
    }
    return (neg ? "-" : "") + t;
  };

  const display = (s: string): string => {
    const g = groupThousands(s);
    return money ? g.replace(".", ",") : g;
  };

  const handle = (e: React.ChangeEvent<HTMLInputElement>) => {
    const el = e.target;
    const caret = el.selectionStart ?? el.value.length;
    // Считаем цифры и десятичный разделитель слева от курсора — по ним и восстанавливаем.
    const sig = decimal ? /[\d.,]/ : /\d/;
    const sigLeft = el.value.slice(0, caret).split("").filter((c) => sig.test(c)).length;
    const next = strip(el.value);
    setDraft(next);
    onChange(next);
    // Восстанавливаем позицию курсора после ре-рендера с новой группировкой.
    requestAnimationFrame(() => {
      const node = ref.current;
      if (!node) return;
      const g = display(next);
      let pos = 0, seen = 0;
      while (pos < g.length && seen < sigLeft) { if (sig.test(g[pos])) seen++; pos++; }
      node.setSelectionRange(pos, pos);
    });
  };

  return (
    <input ref={ref} id={id} className={className} inputMode={decimal ? "decimal" : "numeric"}
      placeholder={placeholder} disabled={disabled} title={title}
      value={display(focused ? shown : pad(shown))} onChange={handle}
      // На фокусе оставляем «,00» в тексте, чтобы поле не прыгало; редактируется как обычно.
      onFocus={() => { setFocused(true); setDraft(pad(shown)); }}
      onBlur={() => setFocused(false)} />
  );
}
